'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type {
  FilterState,
  IconProps,
  PackagesFilterProps,
  SelectFieldProps,
  SelectOption,
} from '../../types';
import RichText from '@/components/ui/RichText/RichText';

const DEFAULT_CATEGORIES: SelectOption[] = [
  { label: 'All categories', value: '' },
  { label: 'General Checkup', value: 'general' },
  { label: 'Heart & Cardio', value: 'cardio' },
  { label: 'Diabetes', value: 'diabetes' },
];

const DEFAULT_GENDERS: SelectOption[] = [
  { label: 'Any gender', value: '' },
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
];

const DEFAULT_PRICE_RANGES: SelectOption[] = [
  { label: 'Any price', value: '' },
  { label: 'Under £50', value: 'under-50' },
  { label: '£50–£100', value: '50-100' },
  { label: '£100+', value: '100-plus' },
];

const EMPTY_FILTER: FilterState = {
  category: '',
  gender: '',
  priceRange: '',
  query: '',
};

export default function PackagesFilter({
  heading,
  description,
  categories = DEFAULT_CATEGORIES,
  genders = DEFAULT_GENDERS,
  priceRanges = DEFAULT_PRICE_RANGES,
  value = {},
  onChange = (): void => {},
  onClear,
}: PackagesFilterProps): React.ReactElement {
  const v: FilterState = {
    category: value?.category || '',
    gender: value?.gender || '',
    priceRange: value?.priceRange || '',
    query: value?.query || '',
  };

  // The search query is debounced before it reaches `onChange` — typing
  // otherwise recomputes the full filtered package list (and re-renders
  // every visible PackageCard) on every keystroke. `queryInput` tracks the
  // input's instant value so typing itself never feels delayed.
  const [queryInput, setQueryInput] = useState(v.query);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // The query changed externally (e.g. Reset filters) — drop any pending
    // debounced update so it can't fire later and undo the external change.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setQueryInput(v.query);
  }, [v.query]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const isFiltered =
    (v.category !== '' && v.category !== 'all') ||
    (v.gender !== '' && v.gender !== 'all') ||
    (v.priceRange !== '' && v.priceRange !== 'all') ||
    queryInput !== '';

  const set = useCallback(
    (patch: Partial<FilterState>): void => onChange({ ...v, ...patch }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [v.category, v.gender, v.priceRange, v.query, onChange],
  );

  const handleQueryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const next = e.target.value;
      setQueryInput(next);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        set({ query: next });
      }, 300);
    },
    [set],
  );

  const handleClear = useCallback((): void => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setQueryInput('');
    onClear ? onClear() : onChange(EMPTY_FILTER);
  }, [onClear, onChange]);

  return (
    <section id="package-enquire" className="content-wrapper scroll-m-36">
      {/* Header */}
      {(heading?.content || description?.content) && (
        <div className="mb-5 space-y-3 text-center">
          {heading?.content && (
            <RichText styles={heading.styles}>{heading.content}</RichText>
          )}
          {description?.content && (
            <RichText styles={description.styles}>
              {description.content}
            </RichText>
          )}
        </div>
      )}

      {/* Card */}
      <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3 shadow-sm md:px-4 md:py-3">
        <div className="grid grid-cols-3 gap-3 md:flex md:flex-wrap md:items-center md:gap-4">
          {/* Filter label */}
          <span className="col-span-3 ml-1 text-sm font-semibold text-slate-500 md:mr-2 md:ml-0 md:text-[15px]">
            Filter by
          </span>

          {/* Selects */}
          <div className="col-span-1 min-w-0 md:w-auto">
            <SelectField
              label="Category"
              value={v.category}
              onChange={(e) => set({ category: e.target.value })}
              options={categories}
              placeholder="Category"
            />
          </div>
          <div className="col-span-1 min-w-0 md:w-auto">
            <SelectField
              label="Gender"
              value={v.gender}
              onChange={(e) => set({ gender: e.target.value })}
              options={genders}
              placeholder="Gender"
            />
          </div>
          <div className="col-span-1 min-w-0 md:w-auto">
            <SelectField
              label="Price range"
              value={v.priceRange}
              onChange={(e) => set({ priceRange: e.target.value })}
              options={priceRanges}
              placeholder="Price"
            />
          </div>

          {/* Search */}
          <div className="col-span-3 md:flex-1">
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                <SearchIcon className="h-4 w-4" />
              </span>
              <input
                type="text"
                inputMode="search"
                value={queryInput}
                onChange={handleQueryChange}
                placeholder="Search by Test Name"
                className="font-regular w-full rounded-xl border border-slate-200 py-2.5 pr-3 pl-9 text-base placeholder-slate-400 focus:border-slate-300 focus:ring-4 focus:ring-slate-100 focus:outline-none"
                aria-label="Search by Test Name"
              />
            </div>
          </div>

          {/* Clear — only shown when a filter is active */}
          {isFiltered && (
            <div className="col-span-3 text-right md:ml-auto">
              <button
                type="button"
                onClick={handleClear}
                className="cursor-pointer text-[15px] font-semibold text-rose-600 hover:text-rose-700 focus:outline-none active:text-rose-800"
              >
                Clear Filter
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function SelectField({
  label,
  options,
  value,
  onChange,
  placeholder,
}: SelectFieldProps): React.ReactElement {
  return (
    <label className="relative inline-flex w-full items-center md:w-auto">
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={onChange}
        className="font-regular w-full appearance-none rounded-xl border border-slate-200 bg-white py-2.5 pr-9 pl-3 text-[15px] text-slate-700 focus:border-slate-300 focus:ring-4 focus:ring-slate-100 focus:outline-none"
        aria-label={label}
      >
        {placeholder && (
          <option value="" disabled hidden>
            {placeholder}
          </option>
        )}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-2.5 text-slate-400">
        <ChevronDownIcon className="h-4 w-4" />
      </span>
    </label>
  );
}

function SearchIcon({ className = 'w-4 h-4' }: IconProps): React.ReactElement {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
      />
    </svg>
  );
}

function ChevronDownIcon({
  className = 'w-4 h-4',
}: IconProps): React.ReactElement {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M5.23 7.21a.75.75 0 011.06.02L10 10.17l3.71-2.94a.75.75 0 111.04 1.08l-4.24 3.36a.75.75 0 01-.94 0L5.21 8.29a.75.75 0 01.02-1.08z"
        clipRule="evenodd"
      />
    </svg>
  );
}
